import io from 'socket.io-client';

// WebSocket connection states
export const ConnectionState = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
};

class SocketManager {
  constructor() {
    this.socket = null;
    this.connectionState = ConnectionState.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.messageQueue = [];
    this.eventHandlers = new Map();
    this.stateChangeHandlers = [];
    this.lastHeartbeat = Date.now();
    this.heartbeatInterval = null;
    this.heartbeatTimeout = 90000; // 90 seconds
  }

  // Initialize connection with options
  connect(url, options = {}) {
    if (this.socket && this.socket.connected) {
      console.log('Socket already connected');
      return;
    }

    const defaultOptions = {
      transports: ['websocket', 'polling'],
      reconnection: false, // We'll handle reconnection manually
      auth: {
        token: localStorage.getItem('accessToken')
      }
    };

    this.socket = io(url, { ...defaultOptions, ...options });
    this.setupEventHandlers();
    this.updateConnectionState(ConnectionState.CONNECTING);
  }

  // Setup core event handlers
  setupEventHandlers() {
    // Connection events
    this.socket.on('connect', () => {
      console.log('WebSocket connected', { socketId: this.socket.id });
      this.updateConnectionState(ConnectionState.CONNECTED);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.flushMessageQueue();
      this.startHeartbeatMonitoring();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected', { reason });
      this.updateConnectionState(ConnectionState.DISCONNECTED);
      this.stopHeartbeatMonitoring();
      
      // Auto-reconnect unless explicitly disconnected
      if (reason !== 'io client disconnect') {
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error', { error: error.message });
      this.updateConnectionState(ConnectionState.ERROR);
      this.attemptReconnect();
    });

    // Heartbeat handling
    this.socket.on('heartbeat', (data) => {
      this.lastHeartbeat = Date.now();
      console.log('Heartbeat received', { timestamp: data.timestamp });
    });

    // Re-emit all registered events
    this.eventHandlers.forEach((handlers, event) => {
      this.socket.on(event, (...args) => {
        handlers.forEach(handler => handler(...args));
      });
    });
  }

  // Update connection state and notify listeners
  updateConnectionState(newState) {
    if (this.connectionState === newState) return;
    
    const oldState = this.connectionState;
    this.connectionState = newState;
    
    this.stateChangeHandlers.forEach(handler => {
      handler(newState, oldState);
    });
  }

  // Reconnection with exponential backoff
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.updateConnectionState(ConnectionState.ERROR);
      return;
    }

    this.reconnectAttempts++;
    this.updateConnectionState(ConnectionState.RECONNECTING);

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      if (this.socket && !this.socket.connected) {
        this.socket.connect();
      }
    }, delay);
  }

  // Message queue management
  queueMessage(event, data) {
    if (this.isConnected()) {
      this.emit(event, data);
    } else {
      console.log('Queueing message for later delivery', { event });
      this.messageQueue.push({ event, data, timestamp: Date.now() });
    }
  }

  flushMessageQueue() {
    if (this.messageQueue.length === 0) return;

    console.log(`Flushing ${this.messageQueue.length} queued messages`);
    
    const messages = [...this.messageQueue];
    this.messageQueue = [];

    messages.forEach(({ event, data }) => {
      this.emit(event, data);
    });
  }

  // Heartbeat monitoring
  startHeartbeatMonitoring() {
    this.stopHeartbeatMonitoring();
    
    this.heartbeatInterval = setInterval(() => {
      const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
      
      if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
        console.warn('Heartbeat timeout - reconnecting');
        this.socket.disconnect();
        this.socket.connect();
      }
    }, 30000); // Check every 30 seconds
  }

  stopHeartbeatMonitoring() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Event handling
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    
    this.eventHandlers.get(event).push(handler);
    
    if (this.socket) {
      this.socket.on(event, handler);
    }
  }

  off(event, handler) {
    if (!this.eventHandlers.has(event)) return;
    
    const handlers = this.eventHandlers.get(event);
    const index = handlers.indexOf(handler);
    
    if (index !== -1) {
      handlers.splice(index, 1);
    }
    
    if (this.socket) {
      this.socket.off(event, handler);
    }
  }

  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit', { event });
    }
  }

  // State change listeners
  onStateChange(handler) {
    this.stateChangeHandlers.push(handler);
    
    // Return unsubscribe function
    return () => {
      const index = this.stateChangeHandlers.indexOf(handler);
      if (index !== -1) {
        this.stateChangeHandlers.splice(index, 1);
      }
    };
  }

  // Utility methods
  isConnected() {
    return this.socket && this.socket.connected;
  }

  getConnectionState() {
    return this.connectionState;
  }

  getSocketId() {
    return this.socket ? this.socket.id : null;
  }

  // Clean disconnect
  disconnect() {
    this.stopHeartbeatMonitoring();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.messageQueue = [];
    this.updateConnectionState(ConnectionState.DISCONNECTED);
  }

  // Force reconnect
  reconnect() {
    this.disconnect();
    this.reconnectAttempts = 0;
    
    setTimeout(() => {
      if (this.socket) {
        this.socket.connect();
      }
    }, 100);
  }
}

// Singleton instance
let socketManagerInstance = null;

export function getSocketManager() {
  if (!socketManagerInstance) {
    socketManagerInstance = new SocketManager();
  }
  return socketManagerInstance;
}

// React hook for socket connection
export function useSocket() {
  const manager = getSocketManager();
  
  return {
    connect: (url, options) => manager.connect(url, options),
    disconnect: () => manager.disconnect(),
    on: (event, handler) => manager.on(event, handler),
    off: (event, handler) => manager.off(event, handler),
    emit: (event, data) => manager.emit(event, data),
    queueMessage: (event, data) => manager.queueMessage(event, data),
    isConnected: () => manager.isConnected(),
    getState: () => manager.getConnectionState(),
    onStateChange: (handler) => manager.onStateChange(handler)
  };
}